/**
 * SeedReviewView
 * ─────────────────────────────────────────────────────────────────────────────
 * Ops-only dashboard page for reviewing community-submitted vendor seeds.
 *
 * Lives at /admin/seed-review (registered in routes.js behind superuser +
 * supervisor sidebars). Surfaces `pending` and `info_required` submissions
 * by default with filters for status, seeder, and submission date range.
 *
 * Per-row actions: expand → per-product QC toggles → Approve / Request info /
 * Reject (with reason). All actions hit /api/v1/seeds/ops/seeds/:id/* endpoints.
 *
 * Icon conventions: Font Awesome + nc-icon only (no emoji in UI per house style).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Spinner,
  Alert,
  Modal,
  Table,
} from "react-bootstrap";
import moment from "moment";
import {
  apiSeedListForReview,
  apiSeedGetForReview,
  apiSeedApprove,
  apiSeedReject,
  apiSeedRequestInfo,
} from "../api/index";

const STATUS_CHIPS = [
  { value: "pending", label: "Pending" },
  { value: "info_required", label: "Info requested" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_BADGE = {
  pending: { variant: "warning", label: "Pending" },
  info_required: { variant: "info", label: "Info requested" },
  approved: { variant: "success", label: "Approved" },
  rejected: { variant: "danger", label: "Rejected" },
  draft: { variant: "secondary", label: "Draft" },
};

function UGX(n) {
  return new Intl.NumberFormat("en-UG").format(Math.round(Number(n || 0)));
}

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || { variant: "secondary", label: status };
  return <Badge bg={cfg.variant}>{cfg.label}</Badge>;
}

// ---------------------------------------------------------------------------
// Review modal — opened per row. Owns its own hydration + action handlers.
// ---------------------------------------------------------------------------
function SeedReviewModal({ show, onHide, seedId, onResolved }) {
  const [loading, setLoading] = useState(false);
  const [seed, setSeed] = useState(null);
  const [error, setError] = useState(null);
  const [qcResults, setQcResults] = useState({}); // productId -> {qcStatus, qcNotes}
  const [opsNotes, setOpsNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [mode, setMode] = useState("review"); // review | reject | requestInfo
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const load = useCallback(async () => {
    if (!seedId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiSeedGetForReview(seedId);
      setSeed(data.seed);
      // Pre-fill each product's QC as "passed" (happy-path default).
      const initial = {};
      (data.seed?.products || []).forEach((p) => {
        initial[p._id] = { qcStatus: p.qcStatus === "failed" ? "failed" : "passed", qcNotes: p.qcNotes || "" };
      });
      setQcResults(initial);
      setOpsNotes(data.seed?.opsNotes || "");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load seed");
    } finally {
      setLoading(false);
    }
  }, [seedId]);

  useEffect(() => {
    if (show) {
      setMode("review");
      setRejectionReason("");
      setSuccessMsg(null);
      load();
    }
  }, [show, load]);

  const toggleQc = (productId, qcStatus) =>
    setQcResults((prev) => ({ ...prev, [productId]: { ...prev[productId], qcStatus } }));

  const setQcNote = (productId, qcNotes) =>
    setQcResults((prev) => ({ ...prev, [productId]: { ...prev[productId], qcNotes } }));

  const handleApprove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const productQcResults = Object.entries(qcResults).map(([productId, r]) => ({
        productId,
        qcStatus: r.qcStatus || "passed",
        qcNotes: r.qcNotes || undefined,
      }));
      const { data } = await apiSeedApprove(seedId, { productQcResults, opsNotes });
      setSuccessMsg(
        `Approved. Credits: UGX ${UGX(data.creditsAwarded || 0)}. Badges: ${
          (data.badgesAwarded || []).join(", ") || "none"
        }.`
      );
      onResolved?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Approval failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError("Rejection reason is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiSeedReject(seedId, { rejectionReason, opsNotes });
      setSuccessMsg("Rejected.");
      onResolved?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Rejection failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!opsNotes.trim()) {
      setError("Please provide the info needed from the seeder");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiSeedRequestInfo(seedId, { opsNotes });
      setSuccessMsg("Info request sent to seeder.");
      onResolved?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-seedling mr-2" aria-hidden="true" /> Review seed submission
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        )}
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert variant="success" className="mb-3">
            {successMsg}
          </Alert>
        )}

        {seed && !loading && (
          <>
            <Row className="mb-3">
              <Col md={8}>
                <h5 className="mb-1">
                  {seed.vendor?.shopName || "(no shop name)"}{" "}
                  <StatusBadge status={seed.status} />
                </h5>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Owner: {seed.vendor?.ownerName || "—"} · MTN: {seed.vendor?.mtnNumber || "—"} ·
                  Type: {seed.targetVendorType}
                </div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Submitted {seed.submittedAt ? moment(seed.submittedAt).fromNow() : "—"} by
                  seeder {String(seed.seederUserId).slice(-6)}
                </div>
              </Col>
              <Col md={4} className="text-right">
                {seed.vendor?.storefrontPhotoUrl && (
                  <img
                    src={seed.vendor.storefrontPhotoUrl}
                    alt="storefront"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 120,
                      borderRadius: 6,
                      objectFit: "cover",
                    }}
                  />
                )}
              </Col>
            </Row>

            {seed.vendor?.locationText && (
              <div className="mb-2">
                <i className="fa fa-map-marker-alt mr-1" /> {seed.vendor.locationText}
              </div>
            )}

            <h6 className="mt-3 mb-2">
              Products ({(seed.products || []).length})
            </h6>
            <Table size="sm" responsive bordered>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Photo</th>
                  <th>Name</th>
                  <th style={{ width: 110 }}>Price</th>
                  <th style={{ width: 200 }}>QC</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {(seed.products || []).map((p) => (
                  <tr key={p._id}>
                    <td>
                      {p.photoUrl ? (
                        <img
                          src={p.photoUrl}
                          alt={p.productName}
                          style={{
                            width: 48,
                            height: 48,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        <i className="fa fa-image text-muted" />
                      )}
                    </td>
                    <td>
                      <div>{p.productName}</div>
                      <small className="text-muted">
                        {p.category || ""} {p.unit ? `· ${p.unit}` : ""}
                      </small>
                    </td>
                    <td>{p.priceUgx ? `UGX ${UGX(p.priceUgx)}` : "—"}</td>
                    <td>
                      <Form.Check
                        inline
                        type="radio"
                        name={`qc-${p._id}`}
                        id={`qc-${p._id}-pass`}
                        label="Pass"
                        checked={qcResults[p._id]?.qcStatus === "passed"}
                        onChange={() => toggleQc(p._id, "passed")}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        name={`qc-${p._id}`}
                        id={`qc-${p._id}-fail`}
                        label="Fail"
                        checked={qcResults[p._id]?.qcStatus === "failed"}
                        onChange={() => toggleQc(p._id, "failed")}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        placeholder="Optional"
                        value={qcResults[p._id]?.qcNotes || ""}
                        onChange={(e) => setQcNote(p._id, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
                {(seed.products || []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      No products
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <Form.Group className="mt-3">
              <Form.Label>
                Ops notes {mode === "requestInfo" && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={opsNotes}
                onChange={(e) => setOpsNotes(e.target.value)}
                placeholder="Internal notes — shared with seeder on info-requests"
              />
            </Form.Group>

            {mode === "reject" && (
              <Form.Group className="mt-2">
                <Form.Label>
                  Rejection reason <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Shown to the seeder in the rejection notification"
                />
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Close
        </Button>
        {seed?.status === "pending" && mode === "review" && (
          <>
            <Button variant="info" onClick={() => setMode("requestInfo")} disabled={submitting}>
              <i className="fa fa-question-circle mr-1" /> Request info
            </Button>
            <Button variant="danger" onClick={() => setMode("reject")} disabled={submitting}>
              <i className="fa fa-times-circle mr-1" /> Reject
            </Button>
            <Button variant="success" onClick={handleApprove} disabled={submitting}>
              {submitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <i className="fa fa-check-circle mr-1" /> Approve
                </>
              )}
            </Button>
          </>
        )}
        {seed?.status === "pending" && mode === "reject" && (
          <>
            <Button variant="secondary" onClick={() => setMode("review")} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReject} disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : "Confirm rejection"}
            </Button>
          </>
        )}
        {seed?.status === "pending" && mode === "requestInfo" && (
          <>
            <Button variant="secondary" onClick={() => setMode("review")} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="info" onClick={handleRequestInfo} disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : "Send request"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------
export default function SeedReviewView() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiSeedListForReview({ status: statusFilter, page, limit: 20 });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load seeds");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingCount = useMemo(() => rows.filter((r) => r.status === "pending").length, [rows]);

  return (
    <div className="content">
      <Container fluid>
        <Row>
          <Col md={12}>
            <Card>
              <Card.Header>
                <Card.Title as="h4">
                  <i className="fa fa-seedling mr-2" /> Seed submissions
                </Card.Title>
                <p className="card-category">
                  Review and approve community-submitted vendors. Approvals
                  create the vendor profile and credit the seeder.
                </p>
              </Card.Header>
              <Card.Body>
                {/* Filter chips */}
                <div className="mb-3">
                  {STATUS_CHIPS.map((c) => (
                    <Button
                      key={c.value}
                      size="sm"
                      variant={statusFilter === c.value ? "primary" : "outline-primary"}
                      className="mr-2 mb-1"
                      onClick={() => {
                        setStatusFilter(c.value);
                        setPage(1);
                      }}
                    >
                      {c.label}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="ml-2"
                    onClick={load}
                    disabled={loading}
                  >
                    <i className="fa fa-sync-alt" /> Refresh
                  </Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="fa fa-inbox fa-2x mb-2" />
                    <div>No submissions in this filter.</div>
                  </div>
                ) : (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Shop</th>
                        <th>Owner</th>
                        <th>MTN</th>
                        <th>Type</th>
                        <th>Products</th>
                        <th>Submitted</th>
                        <th>Status</th>
                        <th style={{ width: 110 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r._id}>
                          <td>{r.vendorSummary?.shopName || "—"}</td>
                          <td>{r.vendorSummary?.ownerName || "—"}</td>
                          <td>{r.vendorSummary?.mtnNumber || "—"}</td>
                          <td>
                            <Badge bg="secondary">{r.targetVendorType}</Badge>
                          </td>
                          <td>{r.productCount}</td>
                          <td>{r.submittedAt ? moment(r.submittedAt).fromNow() : "—"}</td>
                          <td>
                            <StatusBadge status={r.status} />
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setSelectedId(r._id)}
                            >
                              Review
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">
                    {total} total · page {page} of {pages}
                    {pendingCount > 0 && (
                      <span className="ml-2 text-warning">
                        <i className="fa fa-exclamation-circle" /> {pendingCount} awaiting review
                      </span>
                    )}
                  </small>
                  <div>
                    <Button
                      size="sm"
                      variant="light"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <i className="fa fa-chevron-left" /> Prev
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="light"
                      disabled={page >= pages || loading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next <i className="fa fa-chevron-right" />
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <SeedReviewModal
          show={!!selectedId}
          seedId={selectedId}
          onHide={() => setSelectedId(null)}
          onResolved={() => {
            setSelectedId(null);
            load();
          }}
        />
      </Container>
    </div>
  );
}
